import React, { useState } from "react";
import dayjs from "dayjs";
import CustomModal from "../../../Core/CommonV2/CustomModal";
import { CrossIcon } from "../../../Core/svgV2/CrossIcon";
import CustomButton from "../../../Core/CommonV2/CustomButton";
import CustomAvatar from "../../../Core/CommonV2/CustomAvatar";
import CustomSearchBox from "../../../Core/CommonV2/CustomSearchBox";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import useSearch from "../../../Core/hooks/useSearch";
import {
  updateConversationsMultiple,
  resetConversations,
} from "../../../Core/redux/slices/conversations.slice";
import { resetVault } from "../../../Core/redux/slices/vault.slice";
import { sendMessageAPI } from "../../../Core/redux/api/v3/conversationAPI";
import { forwardMessageToUsers } from "../../../Core/redux/api/conversationAPI";
import { updateDashboard } from "../../../Core/redux/slices/dashboard.slice";
import { JOIN_CHAT_EVENT } from "../../../Core/utils/socketConstants";
import { useSearchParams } from "react-router-dom";
import { ObjectID } from "bson";
import { useNavigate } from "react-router-dom";
import { chatClient } from "../../../Core/utils/axiosClient";

const CheckboxIcon = ({ checked }) => (
  <div
    className="w-4 h-4 rounded-[4px] border border-primary-600 bg-white flex items-center justify-center flex-shrink-0 transition-colors"
  >
    {checked && (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path
          d="M1 4L3.5 6.5L9 1"
          stroke="#165ABF"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )}
  </div>
);

// Helper for display name, shared between the row and the selected-names summary.
const getContactDisplayName = (contact) =>
  contact?.isGroup
    ? contact?.name
    : `${contact?.firstname ?? ""} ${contact?.lastname ?? ""}`.trim();

const ChatCardSelect = ({ contact, checked, handleCheckboxChange }) => {
  const isGroup = !!contact?.isGroup;
  const displayName = getContactDisplayName(contact);

  const avatarSrc = isGroup
    ? contact.groupProfilePicture
    : contact.profilePicture;

  const timeSource = contact?.lastMessageUpdate || contact?.lastMessageTime;
  const timeLabel = timeSource ? dayjs(timeSource).fromNow() : "";

  return (
    <div
      className="flex px-4 py-2 gap-x-3 items-center cursor-pointer hover:bg-gray-50"
      onClick={handleCheckboxChange}
    >
      <CheckboxIcon checked={checked} />
      <CustomAvatar
        src={avatarSrc}
        title={displayName}
        size={40}
        badge
        whiteText
      />
      <div className="flex flex-col gap-y-0.5 w-full min-w-0">
        <div className="flex gap-x-2 items-center">
          <p className="text-black out-500-14 leading-5 truncate">{displayName}</p>
          <p className="text-gray-500 out-300-12 flex-shrink-0">{timeLabel}</p>
        </div>
        <div className="text-gray-500 out-300-12 truncate">
          {contact?.lastMessage}
        </div>
      </div>
    </div>
  );
};

const ContactsList = ({ contacts, selectedContacts, handleCheckboxChange }) => (
  <div className="flex flex-col gap-y-1.5">
    <div className="out-500-14 text-black px-4">Recent Chats</div>
    <div className="flex flex-col max-h-[280px] overflow-y-auto custom-scroll">
      {contacts?.length ? (
        contacts.map((contact) => (
          <ChatCardSelect
            key={contact.threadId}
            contact={contact}
            checked={selectedContacts.some(
              (c) => +c.threadId === +contact.threadId
            )}
            handleCheckboxChange={() => handleCheckboxChange(contact)}
          />
        ))
      ) : (
        <div className="py-6 text-center text-gray-500 out-300-14">
          No chats found
        </div>
      )}
    </div>
  </div>
);

const ForwardMessageModal = ({ socket }) => {
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();
  const { threads, shareMessageModal, forwardMessage } = useAppSelector(
    (state) => state.conversations
  );
  const { user } = useAppSelector((state) => state.userDetails);
  const { shareInMessageModal } = useAppSelector((state) => state.vault);
  const navigate = useNavigate();
  const isVaultShare = !!shareInMessageModal?.visible;

  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const handleCheckboxChange = (contact) => {
    setSelectedContacts((prev) =>
      prev.some((c) => +c.threadId === +contact.threadId)
        ? prev.filter((c) => +c.threadId !== +contact.threadId)
        : [...prev, contact]
    );
  };

  const { search, setSearch, filteredData } = useSearch(threads, [
    "name",
    "firstname",
    "lastname",
  ]);

  const handleCloseModal = () => {
    setSelectedContacts([]);
    setSearch("");
    dispatch(resetConversations(["shareMessageModal", "forwardMessage", "shareType"]));
    if (isVaultShare) {
      dispatch(resetVault(["shareInMessageModal"]));
    }
  };

  console.log(user, 'user')
  const handleForwardChatMessage = async () => {
    const originalMessageId =
      forwardMessage.original_message_id || forwardMessage._id;
    await Promise.all(
      selectedContacts.map((contact) => {
        const objectId = new ObjectID();
        const forwardedMessage = {
          _id: objectId.toString(),
          original_message_id: originalMessageId,
          threadId: +contact.threadId,
          content: forwardMessage.content || forwardMessage.message,
          senderId: user.id,
          senderName: `${user.firstName} ${user.lastName}`,
          sender: user.id,
          forwarded: true,
          attachments: forwardMessage.attachments || [],
          replyMessage: {},
          reactions: {},
          notifyToUsers: contact.participants || [contact.userId],
          threadName: getContactDisplayName(contact),
          tenantId: user.tenant_id ?? forwardMessage.tenantId,
          isGroup: contact.isGroup || false,
          createdAt: new Date().toISOString(),
        };
        return sendMessageAPI({ message: forwardedMessage, forwarded: true });
      })
    );

    const primaryContact = selectedContacts[0];
    const isGroup = !!primaryContact.isGroup;

    dispatch(
      updateConversationsMultiple([
        {
          key: "currentThread",
          value: { ...primaryContact, type: isGroup ? "group" : "private", limit: 20, offset: 0 },
        },
        { key: "currentThreadMessage", value: primaryContact.messages?.length > 0 ? primaryContact.messages : [] },
      ])
    );
    dispatch(resetConversations(["replyMessage", "editMessage"]));

    if (isGroup) {
      socket.emit("joinroom", {
        newThread: primaryContact.threadId,
        prevThread: undefined, // ChatCard reads currentThread.threadId here, pre-dispatch — grab it before overwriting if you need it
        joiningUserId: user.id,
      });
    } else {
      await chatClient.post("/conversation/read-messages", {
        userId: user.id,
        chatId: primaryContact.threadId,
        user,
      });
    }
  };

  const handleShareVaultItem = async () => {
    const messages = selectedContacts.map((checked) => ({
      message:
        shareInMessageModal.type === "folder" || shareInMessageModal.type === "link"
          ? shareInMessageModal.url
          : "",
      senderName: `${user.firstName} ${user.lastName}`,
      createdAt: new Date(),
      senderProfilePicture: user.profilePicture,
      replyMessage: {},
      attachments:
        shareInMessageModal.type === "file" ? shareInMessageModal.file : [],
      senderId: user.id,
      threadId: +checked.threadId,
    }));

    await dispatch(forwardMessageToUsers({ messages }));

    dispatch(
      updateDashboard({
        key: "alertPopupModal",
        value: {
          visible: true,
          data: {
            title: "Shared Successfully",
            subtitle: `Successfully shared to ${selectedContacts.length} chat${selectedContacts.length > 1 ? "s" : ""
              }`,
            description: "",
          },
        },
      })
    );
  };

  const handleForward = async () => {
    if (!selectedContacts.length) return;

    setLoading(true);
    try {
      if (isVaultShare) {
        await handleShareVaultItem();
      } else {
        if (!forwardMessage?._id) return;
        await handleForwardChatMessage();
      }
      handleCloseModal();
    } catch (err) {
      console.error("Forward failed:", err);
      dispatch(
        updateDashboard({
          key: "alertPopupModal",
          value: {
            visible: true,
            data: {
              title: "Forward failed",
              subtitle: "Something went wrong. Please try again.",
              description: "",
            },
          },
        })
      );
    } finally {
      setLoading(false);
    }
  };

  if (!shareMessageModal) return null;

  // FIX 3 — selected-recipients summary text shown next to the buttons,
  // truncating with an ellipsis when it overflows.
  const selectedNamesLabel = selectedContacts
    .map((c) => getContactDisplayName(c))
    .filter(Boolean)
    .join(", ");

  return (
    <CustomModal
      visible={shareMessageModal}
      onCancel={handleCloseModal}
      width={"420px"}
      body={
        <div className="w-full flex flex-col py-4 gap-y-3">
          <div className="flex px-4 justify-between items-center">
            <p className="out-500-16 text-black">
              {isVaultShare ? "Send to" : "Forward to"}
            </p>
            <CrossIcon
              className={`text-gray-700 ${loading ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
                }`}
              role="Button"
              onClick={loading ? undefined : handleCloseModal}
            />
          </div>

          <div className="px-4">
            {/* <div
              className={`rounded-[16px] border transition-colors ${searchFocused ? "border-primary-600" : "border-transparent"
                }`}
              onFocusCapture={() => setSearchFocused(true)}
              onBlurCapture={() => setSearchFocused(false)}
            > */}
            <CustomSearchBox
              className="bg-gray-50 flex w-full"
              search={search}
              setSearch={setSearch}
            />
            {/* </div> */}
          </div>

          <div className={loading ? "opacity-50 pointer-events-none" : ""}>
            <ContactsList
              contacts={filteredData}
              selectedContacts={selectedContacts}
              handleCheckboxChange={handleCheckboxChange}
            />
          </div>

          {/* FIX 2 + FIX 3 — footer: selected-names summary on the left,
              buttons resized to Figma's 30px/radius-8, Cancel as a
              borderless "tertiary gray" style. */}
          <div className="flex px-4 justify-between items-center gap-x-3 pt-1">
            <p className="flex-1 min-w-0 truncate out-300-12 text-gray-800">
              {selectedNamesLabel}
            </p>
            <div className="flex items-center gap-x-3 flex-shrink-0">
              <CustomButton
                text={"Cancel"}
                height={"30px"}
                disabled={loading}
                className="text-gray-700 out-500-14 rounded-[8px] hover:bg-gray-50"
                onClick={handleCloseModal}
              />
              <CustomButton
                text={loading ? "Forwarding..." : isVaultShare ? "Share" : "Forward"}
                height={"30px"}
                disabled={!selectedContacts.length || loading}
                className={`out-500-14 rounded-[8px] ${selectedContacts.length && !loading
                  ? "text-white bg-primary-600"
                  : "text-gray-400 bg-gray-100 cursor-not-allowed"
                  }`}
                onClick={handleForward}
              />
            </div>
          </div>
        </div >
      }
    />
  );
};

export default ForwardMessageModal;